import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Accordion, ActionIcon, Badge, Box, Group, Menu, Text } from "@mantine/core";
import { useHover } from "@mantine/hooks";
import { changeLayerConfig, changeLayerPaintConfig } from "../../reducers/layersReducer.js";
import { AIAssistantAvatar, ApiKeyAvatar, UserAvatar } from "../../components/Avatar.js";
import { useStyleEditor } from "../../components/StyleEditorContext.js";
import { isNumber } from "es-toolkit/compat";
import { isString, isUndefined } from "es-toolkit";
import { IconArrowBackUp, IconDotsVertical, IconMessageForward } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { setSetting } from "../../reducers/settingReducer.js";
import { deriveProjectionMode } from "../Settings/ProjectionConfig.js";
import { Event } from "../../constants/events.js";
export const VersionItemTitle = ({ version, published, current, menu }) => {
    const { id, user, apiKey, createdAt, } = version;
    return (_jsxs(Group, { justify: "space-between", pr: "md", children: [_jsxs(Group, { children: [user && (_jsx(UserAvatar, { user: user })), apiKey && (_jsx(ApiKeyAvatar, { name: apiKey.name })), id] }), _jsxs(Group, { children: [published && _jsx(Badge, { color: "green", children: "Published" }), current && _jsx(Badge, { color: "blue", children: "Current" }), createdAt && new Date(createdAt).toLocaleDateString(), menu] })] }));
};
export const propertyValueDisplay = (propertyValue) => {
    if (isString(propertyValue) || isNumber(propertyValue)) {
        return propertyValue;
    }
    return null;
};
export function actionTitle(action, intl) {
    switch (action.type) {
        case changeLayerPaintConfig.type: {
            const { payload: { layerId, propertyLabel, }, } = action;
            return intl.formatMessage({
                defaultMessage: "Changed paint {propertyLabel} of layer {layerId}",
                id: "version.actionTitle.changeLayerPaint",
            }, {
                propertyLabel,
                layerId,
            });
        }
        case changeLayerConfig.type: {
            const { payload: { id, type, }, } = action;
            if (type === "zoom") {
                return intl.formatMessage({
                    defaultMessage: "Changed zoom range of layer {id}",
                    id: "version.actionTitle.changeLayerZoom",
                }, {
                    id,
                });
            }
            return intl.formatMessage({
                defaultMessage: "Changed layer {id} config",
                id: "version.actionTitle.changeLayerConfig",
            }, {
                id,
            });
        }
        case setSetting.type: {
            const { payload: { projection, pitch, bearing, zoom, center, }, } = action;
            if (projection) {
                return intl.formatMessage({
                    defaultMessage: "Changed projection to {projection}",
                    id: "version.actionTitle.changeProjection",
                }, {
                    projection: deriveProjectionMode(projection),
                });
            }
            if (!isUndefined(pitch) && !isUndefined(bearing) && !isUndefined(zoom) && !isUndefined(center)) {
                return intl.formatMessage({
                    defaultMessage: "Changed map origin to {center}/{zoom}/{pitch}/{bearing}",
                    id: "version.actionTitle.changeMapOrigin",
                }, {
                    center: center.map(i => i.toFixed(2)).join("/"),
                    zoom,
                    pitch,
                    bearing,
                });
            }
            if (pitch) {
                return intl.formatMessage({
                    defaultMessage: "Changed pitch to {pitch}",
                    id: "version.actionTitle.changePitch",
                }, {
                    pitch,
                });
            }
            if (bearing) {
                return intl.formatMessage({
                    defaultMessage: "Changed bearing to {bearing}",
                    id: "version.actionTitle.changeBearing",
                }, {
                    bearing,
                });
            }
            if (zoom) {
                return intl.formatMessage({
                    defaultMessage: "Changed zoom to {zoom}",
                    id: "version.actionTitle.changeZoom",
                }, {
                    zoom,
                });
            }
            if (center) {
                return intl.formatMessage({
                    defaultMessage: "Changed center to {center}",
                    id: "version.actionTitle.changeCenter",
                }, {
                    center: center.join("/"),
                });
            }
            return intl.formatMessage({
                defaultMessage: "Changed settings",
                id: "version.actionTitle.changeSettings",
            });
        }
        default: {
            return action.type;
        }
    }
}
export const ActionItemTitle = ({ action, menu }) => {
    const intl = useIntl();
    const title = actionTitle(action, intl);
    return (_jsxs(Group, { justify: "space-between", pr: "md", children: [title, menu] }));
};
export const AssistantVersionItemTitle = ({ text, menu }) => {
    return (_jsxs(Group, { justify: "space-between", pr: "md", wrap: "nowrap", gap: "sm", children: [_jsxs(Group, { flex: 1, miw: 0, wrap: "nowrap", gap: "sm", children: [_jsx(AIAssistantAvatar, {}), _jsx(Text, { truncate: "end", flex: 1, miw: 0, children: text })] }), menu] }));
};
export const VersionListItemControl = ({ action, version, published, current, text, messageId, selected, canRestore, onRestore }) => {
    const intl = useIntl();
    const { emit, captureEvent } = useStyleEditor();
    const { hovered, ref } = useHover();
    const [focused, setFocused] = useState(false);
    const [menuOpened, setMenuOpened] = useState(false);
    const visible = hovered || focused || menuOpened || selected;
    const canShowInChat = isNumber(messageId);
    const handleShowInChat = useCallback((e) => {
        e.stopPropagation();
        if (isNumber(messageId)) {
            emit("scrollToMessage", messageId);
        }
    }, [messageId, emit]);
    const handleTriggerClick = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const handleRestore = useCallback((e) => {
        captureEvent(Event.RestoreVersion);
        e.stopPropagation();
        onRestore();
    }, [version, emit]);
    const handleFocus = useCallback(() => {
        setFocused(true);
    }, []);
    const handleBlur = useCallback(() => {
        setFocused(false);
    }, []);
    const menu = (!canRestore && !canShowInChat)
        ? null
        : (_jsx(Box, { style: {
                opacity: visible ? 1 : 0,
                transition: "opacity 120ms",
                pointerEvents: visible ? "auto" : "none",
            }, children: _jsxs(Menu, { position: "bottom-end", withinPortal: true, opened: menuOpened, onChange: setMenuOpened, children: [_jsx(Menu.Target, { children: _jsx(ActionIcon, { component: "span", variant: "subtle", color: "gray", size: "sm", "aria-label": intl.formatMessage({ defaultMessage: "Version actions", id: "button.versionActions" }), onClick: handleTriggerClick, onFocus: handleFocus, onBlur: handleBlur, children: _jsx(IconDotsVertical, { size: 18, stroke: 1.5 }) }) }), _jsxs(Menu.Dropdown, { children: [canRestore && (_jsx(Menu.Item, { leftSection: _jsx(IconArrowBackUp, { size: 16, stroke: 1.5 }), onClick: handleRestore, children: intl.formatMessage({ defaultMessage: "Restore this version", id: "button.restoreVersion" }) })), canShowInChat && (_jsx(Menu.Item, { leftSection: _jsx(IconMessageForward, { size: 16, stroke: 1.5 }), onClick: handleShowInChat, children: intl.formatMessage({ defaultMessage: "Show in chat", id: "button.showInChat" }) }))] })] }) }));
    return (_jsx(Box, { ref: ref, children: _jsxs(Accordion.Control, { children: [(version && !text && !action) && (_jsx(VersionItemTitle, { version: version, published: published, current: current, menu: menu })), text && (_jsx(AssistantVersionItemTitle, { text: text, menu: menu })), action && (_jsx(ActionItemTitle, { action: action, menu: menu }))] }) }));
};
